import React, { useMemo, useState } from 'react';
import { formatQuantity } from '../utils/unitDisplay';
import { formatLongDate } from './ClosingDatePicker';
import './CcsPreviewModal.css';

/**
 * Continuous Closing Stock — the count preview (spec §5.2, §7.1; plan D2/D3).
 *
 * Shown before a count is applied for a switched-on business. The server ran the whole
 * apply and rolled it back (POST /closing-stock/apply with dryRun), so every figure here
 * is exactly what applying will do:
 *
 *   expected   what the app holds for the item on the count date
 *   counted    what is on the sheet (raw shelf + preparations, per ingredient)
 *   difference what will be recorded (one adjustment per item, dated the count date)
 *   after      changes dated after the count date — they stay; that is the point
 *
 * Two things must be confirmed before Apply is allowed:
 *   K5  a line whose difference is over 300% or 10× expected (a unit slip?) — each one
 *   K1  items NOT on this count that hold over ₹500 or moved in the last 7 days — they
 *       keep their system figure and are not pinned
 */

const fmt = (qty, unit) => {
  const n = Number(qty) || 0;
  try { return formatQuantity(n, unit || 'g'); } catch (_) { return `${n} ${unit || ''}`; }
};
const sign = (n) => (n > 0 ? '+' : '');

export default function CcsPreviewModal({ preview, applying, onCancel, onConfirm }) {
  const flagged = useMemo(() => (preview?.items || []).filter((i) => i.flag_unit_slip), [preview]);
  const [checked, setChecked] = useState({});
  const [leaveUncounted, setLeaveUncounted] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!preview) return null;

  const items = [...(preview.items || [])].sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
  const uncounted = preview.uncounted || [];
  const allFlagsChecked = flagged.every((f) => checked[f.item_id]);
  const canApply = allFlagsChecked && (uncounted.length === 0 || leaveUncounted) && !applying;
  const changed = items.filter((i) => Math.abs(i.difference) > 0.00005).length;
  const shown = showAll ? items : items.slice(0, 60);

  return (
    <div className="cs-modal-overlay ccsp-overlay" role="dialog" aria-modal="true" aria-labelledby="ccsp-title">
      <div className="ccsp-modal">
        <div className="ccsp-title" id="ccsp-title">Check the count before applying</div>
        <div className="ccsp-sub">
          Closing stock for <strong>{formatLongDate(preview.closing_date)}</strong>
          {preview.derived ? <> · counted on <strong>{formatLongDate(preview.count_date)}</strong> (the month&apos;s closing is derived)</> : null}
          . Bills and sales entered after this date stay as they are; only the difference below is recorded.
        </div>
        <div className="ccsp-stats">
          <span><b>{items.length}</b> items counted</span>
          <span><b>{changed}</b> will change</span>
          {flagged.length > 0 && <span className="ccsp-stat-warn"><b>{flagged.length}</b> to check</span>}
          {uncounted.length > 0 && <span className="ccsp-stat-warn"><b>{uncounted.length}</b> not counted</span>}
        </div>

        <div className="ccsp-table-wrap">
          <table className="ccsp-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Expected</th>
                <th className="num">Counted</th>
                <th className="num">Difference</th>
                <th className="num" title="Changes dated after the count date — kept">After count</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.item_id} className={i.flag_unit_slip ? 'ccsp-flag' : ''}>
                  <td>
                    {i.flag_unit_slip && (
                      <label className="ccsp-check">
                        <input
                          type="checkbox"
                          checked={!!checked[i.item_id]}
                          onChange={(e) => setChecked((p) => ({ ...p, [i.item_id]: e.target.checked }))}
                        />
                        <span>checked</span>
                      </label>
                    )}
                    {i.item_name || `#${i.item_id}`}
                    {i.line_state === 'zero' && <span className="ccsp-pill">zero</span>}
                  </td>
                  <td className="num">{fmt(i.expected, i.unit)}</td>
                  <td className="num">{fmt(i.counted, i.unit)}</td>
                  <td className={`num ${i.difference < 0 ? 'ccsp-neg' : i.difference > 0 ? 'ccsp-pos' : ''}`}>
                    {Math.abs(i.difference) > 0.00005 ? `${sign(i.difference)}${fmt(i.difference, i.unit)}` : '—'}
                  </td>
                  <td className="num ccsp-muted">{Math.abs(i.after) > 0.00005 ? `${sign(i.after)}${fmt(i.after, i.unit)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > shown.length && (
            <button type="button" className="ccsp-more" onClick={() => setShowAll(true)}>
              Show all {items.length} items
            </button>
          )}
        </div>

        {flagged.length > 0 && (
          <div className="ccsp-note ccsp-warn">
            {flagged.length} line{flagged.length > 1 ? 's differ' : ' differs'} from expected by more than 300%, or
            {flagged.length > 1 ? ' are' : ' is'} 10× expected — often a unit slip (kg written as g). Tick each one once you have checked it.
          </div>
        )}

        {uncounted.length > 0 && (
          <div className="ccsp-note">
            <div className="ccsp-note-title">Not on this count</div>
            <div className="ccsp-muted">
              These hold over ₹500 or moved in the last 7 days. If they are not counted they keep the app&apos;s figure and are not pinned.
            </div>
            <ul className="ccsp-uncounted">
              {uncounted.slice(0, 30).map((u) => (
                <li key={u.item_id}>
                  {u.name} <span className="ccsp-muted">· ₹{Number(u.value).toFixed(0)}{u.moved_7d ? ' · moved this week' : ''}</span>
                </li>
              ))}
              {uncounted.length > 30 && <li className="ccsp-muted">…and {uncounted.length - 30} more</li>}
            </ul>
            <label className="ccsp-check ccsp-leave">
              <input type="checkbox" checked={leaveUncounted} onChange={(e) => setLeaveUncounted(e.target.checked)} />
              <span>Leave these as not counted</span>
            </label>
          </div>
        )}

        {(preview.skipped || []).length > 0 && (
          <div className="ccsp-note ccsp-warn">
            {preview.skipped.length} row{preview.skipped.length > 1 ? 's' : ''} cannot be applied (unit problems) and will be left out.
          </div>
        )}

        <div className="cs-modal-actions ccsp-actions">
          <button type="button" className="cs-modal-cancel" onClick={onCancel} disabled={applying}>Back to the sheet</button>
          <button type="button" className="cs-modal-confirm" onClick={onConfirm} disabled={!canApply}>
            {applying ? 'Applying…' : 'Apply the count'}
          </button>
        </div>
      </div>
    </div>
  );
}
